import { useState, useEffect } from "react";
import { Modal, Button, List, TextInput, Text, Group, Stack } from "@mantine/core";

export interface ConfirmDialogProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  description: string;
  /** Supporting lines naming the record being destroyed. An array renders
   *  as a bulleted list, one item per entry, with falsy entries skipped —
   *  that is what most call sites pass. Anything else renders as a single
   *  dimmed block. */
  details?: React.ReactNode | React.ReactNode[];

  /** Primary action. Despite the name, not every caller's primary action deletes anything (e.g. a bulk
   *  apply/overwrite) — ``hardDeleteColor`` lets those pass a non-destructive colour instead of the red
   *  default. */
  onHardDelete: () => void;
  hardDeleteLabel?: string;
  isSubmitting?: boolean;
  /** Button colour for the primary action. "red" by default (most callers really do delete something);
   *  pass e.g. "blue" when the action isn't destructive. */
  hardDeleteColor?: string;

  /** Optional secondary action (e.g. inactivate) */
  onSoftDelete?: () => void;
  softDeleteLabel?: string;
  isSoftDeleting?: boolean;

  cancelText?: string;

  /** Require typing a confirmation string before allowing hard delete */
  requireConfirmText?: boolean;
  confirmText?: string;
}

export function ConfirmDialog({
  opened,
  onClose,
  title,
  description,
  details,
  onHardDelete,
  hardDeleteLabel = "Delete",
  isSubmitting = false,
  hardDeleteColor = "red",
  onSoftDelete,
  softDeleteLabel = "Inactivate",
  isSoftDeleting = false,
  cancelText = "Cancel",
  requireConfirmText = false,
  confirmText = "DELETE",
}: ConfirmDialogProps) {
  const [showHardDelete, setShowHardDelete] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");

  useEffect(() => {
    if (!opened) {
      setShowHardDelete(false);
      setConfirmInput("");
    }
  }, [opened]);

  const canHardDelete = !requireConfirmText || confirmInput === confirmText;
  const busy = isSubmitting || isSoftDeleting;

  // Callers overwhelmingly pass an array of lines. Rendering that straight
  // into a <Text> concatenates them into one run-on sentence, so bullet it.
  const detailItems = Array.isArray(details) ? details.filter(Boolean) : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="sm"
    >
      <Stack gap="md">
        <Text fz="sm" c="dimmed">{description}</Text>

        {detailItems
          ? detailItems.length > 0 && (
              <List size="sm" spacing={4} c="dimmed" withPadding>
                {detailItems.map((line, i) => (
                  <List.Item key={i}>{line}</List.Item>
                ))}
              </List>
            )
          : details && (
              // ``component="div"``: callers pass <List>/<ul> here, which a
              // <p> may not contain.
              <Text component="div" fz="sm" c="dimmed">{details}</Text>
            )}

        {requireConfirmText && (
          <div>
            <Text fz="sm" mb="xs">
              Type{" "}
              <Text
                component="span"
                ff="monospace"
                fw={700}
                style={{
                  background: "var(--mantine-color-yellow-light)",
                  border: "1px solid var(--mantine-color-yellow-4)",
                  padding: "1px 6px",
                  borderRadius: 4,
                }}
              >
                {confirmText}
              </Text>
              {" "}to confirm:
            </Text>
            <TextInput
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.currentTarget.value)}
              disabled={busy}
              autoFocus
            />
          </div>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={busy}>
            {cancelText}
          </Button>

          {onSoftDelete && !showHardDelete ? (
            <>
              <Button color="blue" onClick={onSoftDelete} disabled={isSoftDeleting} loading={isSoftDeleting}>
                {softDeleteLabel}
              </Button>
              <Button color={hardDeleteColor} onClick={() => setShowHardDelete(true)} disabled={isSoftDeleting}>
                {hardDeleteLabel}
              </Button>
            </>
          ) : (
            <Button
              color={hardDeleteColor}
              onClick={onHardDelete}
              disabled={isSubmitting || !canHardDelete}
              loading={isSubmitting}
            >
              {hardDeleteLabel}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
